"use client";

import { useCallback, useState } from "react";
import type { SubmittedFormResponse } from "@/features/forms/_logic/viewer-form";

/**
 * A form modal's submission lifecycle. The in-flight submit itself isn't a
 * separate phase — the form stays visible (disabled, via a separate
 * `isSubmitting` flag) while its own POST is pending, rather than swapping views.
 * - `idle` — blank (or draft-restored) form, not yet submitted.
 * - `submitted` — just submitted this session; shows the thank-you state.
 * - `previously-submitted` — an existing response was detected on open, or a
 *   resubmit hit the single-submission conflict; shows the recap state.
 * - `editing` — editing the existing response's prefilled fields.
 */
export type FormSubmissionPhase = "idle" | "submitted" | "previously-submitted" | "editing";

interface UseFormSubmissionStateReturn {
  phase: FormSubmissionPhase;
  recap: SubmittedFormResponse | null;
  markSubmitted: () => void;
  /** Shows the recap for an existing response — on initial detection, a 409 resubmit conflict, or after a successful edit. */
  showRecap: (recap: SubmittedFormResponse) => void;
  startEditing: () => void;
  cancelEditing: () => void;
  reset: () => void;
}

/**
 * Tracks a form modal's submission lifecycle (see {@link FormSubmissionPhase}),
 * extracted out of `FormModal` so the already-submitted-detection and edit-mode
 * behavior don't grow that component's state further.
 *
 * @returns The current phase, the loaded recap (if any), and the transition actions.
 */
export function useFormSubmissionState(): UseFormSubmissionStateReturn {
  const [phase, setPhase] = useState<FormSubmissionPhase>("idle");
  const [recap, setRecap] = useState<SubmittedFormResponse | null>(null);

  const markSubmitted = useCallback(() => setPhase("submitted"), []);

  const showRecap = useCallback((nextRecap: SubmittedFormResponse) => {
    setRecap(nextRecap);
    setPhase("previously-submitted");
  }, []);

  const startEditing = useCallback(() => setPhase("editing"), []);
  const cancelEditing = useCallback(() => setPhase("previously-submitted"), []);

  const reset = useCallback(() => {
    setPhase("idle");
    setRecap(null);
  }, []);

  return {
    phase,
    recap,
    markSubmitted,
    showRecap,
    startEditing,
    cancelEditing,
    reset,
  };
}
